import styled from "styled-components"
import type { ContactPreference, ContactValues } from "../types"

const onlyDigits = (v: string): string => v.replace(/\D/g, "")

/** (11) 99999-8888 */
export const maskPhone = (v: string): string => {
  const d = onlyDigits(v).slice(0, 11)
  if (!d) return ""
  if (d.length <= 2) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/** 000.000.000-00 */
export const maskCpf = (v: string): string => {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type ContactFieldError = "name" | "email" | "whatsapp" | "cpf" | "consent"

const ERROR_MESSAGES: Record<ContactFieldError, string> = {
  name: "Informe nome e sobrenome.",
  email: "E-mail inválido.",
  whatsapp: "WhatsApp incompleto.",
  cpf: "CPF inválido.",
  consent: "É necessário aceitar a Política de privacidade.",
}

/** Regras do protótipo: nome com 2+ palavras, e-mail válido, WhatsApp completo,
    CPF (11 dígitos) quando exigido, e consentimento obrigatório. */
export const validateContactFields = (
  v: ContactValues,
  requireCpf: boolean,
): ContactFieldError[] => {
  const errors: ContactFieldError[] = []
  if (v.name.trim().split(/\s+/).filter(Boolean).length < 2) errors.push("name")
  if (!EMAIL_RE.test(v.email.trim())) errors.push("email")
  if (onlyDigits(v.whatsapp).length < 10) errors.push("whatsapp")
  if (requireCpf && onlyDigits(v.cpf).length !== 11) errors.push("cpf")
  if (!v.consent) errors.push("consent")
  return errors
}

export const validateContact = (v: ContactValues, requireCpf: boolean): boolean =>
  validateContactFields(v, requireCpf).length === 0

const PREFERENCES: { key: ContactPreference; label: string }[] = [
  { key: "email", label: "E-mail" },
  { key: "phone", label: "Telefone" },
  { key: "whatsapp", label: "WhatsApp" },
]

interface ContactFieldsProps {
  values: ContactValues
  onChange: (patch: Partial<ContactValues>) => void
  /** Exibe o campo de CPF (builder). */
  showCpf?: boolean
  /** Exige o preenchimento; false → rótulo "CPF (opcional)". */
  requireCpf?: boolean
  privacyPolicyUrl?: string
  /** Campos com erro (exibidos após clicar em Continuar com dados inválidos). */
  errors?: ContactFieldError[]
}

export const ContactFields = ({
  values,
  onChange,
  showCpf = true,
  requireCpf = true,
  privacyPolicyUrl = "#",
  errors = [],
}: ContactFieldsProps) => {
  const errorFor = (field: ContactFieldError) =>
    errors.includes(field) ? (
      <ErrorMsg role="alert">{ERROR_MESSAGES[field]}</ErrorMsg>
    ) : null

  const togglePreference = (key: ContactPreference) =>
    onChange({
      preferences: values.preferences.includes(key)
        ? values.preferences.filter((p) => p !== key)
        : [...values.preferences, key],
    })

  return (
    <Wrap>
      <Field>
        <Label>Nome completo</Label>
        <Input
          type="text"
          autoComplete="name"
          autoCapitalize="words"
          placeholder="Digite seu nome e sobrenome"
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        {errorFor("name")}
      </Field>

      <Field>
        <Label>E-mail</Label>
        <Input
          type="email"
          autoComplete="email"
          placeholder="Digite seu melhor e-mail"
          value={values.email}
          onChange={(e) => onChange({ email: e.target.value })}
        />
        {errorFor("email")}
      </Field>

      <Field>
        <Label>WhatsApp</Label>
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          maxLength={15}
          placeholder="(00) 00000-0000"
          value={values.whatsapp}
          onChange={(e) => onChange({ whatsapp: maskPhone(e.target.value) })}
        />
        {errorFor("whatsapp")}
      </Field>

      {showCpf && (
        <Field>
          <Label>{requireCpf ? "CPF" : "CPF (opcional)"}</Label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={14}
            placeholder="000.000.000-00"
            value={values.cpf}
            onChange={(e) => onChange({ cpf: maskCpf(e.target.value) })}
          />
          {errorFor("cpf")}
        </Field>
      )}

      <Field>
        <Label>Como prefere receber a confirmação?</Label>
        <CheckGroup>
          {PREFERENCES.map(({ key, label }) => (
            <CheckRow key={key}>
              <input
                type="checkbox"
                checked={values.preferences.includes(key)}
                onChange={() => togglePreference(key)}
              />
              {label}
            </CheckRow>
          ))}
        </CheckGroup>
      </Field>

      <Consent>
        <input
          type="checkbox"
          checked={values.consent}
          onChange={(e) => onChange({ consent: e.target.checked })}
        />
        <span>
          Li e aceito a{" "}
          <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
            Política de privacidade
          </a>{" "}
          e concordo em receber comunicações da concessionária.
        </span>
      </Consent>
      {errorFor("consent")}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`
const Label = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--td-field-label, var(--text-title, #0f172a));
`
const Input = styled.input`
  height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--td-field-border, var(--general-border, #b7c0cd));
  background: var(--td-input-bg, var(--cards-background, #fff));
  font-size: 0.875rem;
  color: var(--td-field-text, var(--text-title, #0f172a));

  &::placeholder {
    color: var(--td-field-placeholder, var(--text-body, #64748b));
  }
`
const ErrorMsg = styled.span`
  font-size: 0.75rem;
  color: var(--general-error, #dc2626);
`
const CheckGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`
const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-title, #0f172a);
`
const Consent = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--general-border, #e2e8f0);
  font-size: 0.8125rem;
  color: var(--text-body, #64748b);

  a {
    color: var(--general-primary, #0027d4);
  }
`
